import { AdProviderTypes, type AdProvidersSettings } from '@/types'
import type { StartRumEvent } from '@/types/logger.types'

import type { AdProvider } from './ad-provider'
import type { FailedAdProvider } from './ad-service'
import { AdProviderStatus, type AdTargeting } from './types'

function createFailedProvider(type: AdProviderTypes, errorMessage: string): FailedAdProvider {
    return { failed: true, type, errorMessage }
}

type ProviderInitConfig = {
    targeting: AdTargeting
    startRumEvent: StartRumEvent
    auid: string
}

type SetProviderCallback = (index: number, provider: AdProvider | FailedAdProvider) => void

function moduleLoadError(type: AdProviderTypes, cause: unknown): Error {
    const reason = cause instanceof Error ? cause.message : String(cause)
    return new Error(`[${type}] module load failed: ${reason}`)
}

/**
 * Creates provider instance from settings.
 */
async function createProvider(settings: AdProvidersSettings, config: ProviderInitConfig): Promise<AdProvider> {
    const targeting = {
        ...config.targeting,
        ...('targeting' in settings.options ? settings.options.targeting : undefined),
    }
    const { type } = settings
    switch (type) {
        case AdProviderTypes.GAME_DISTRIBUTION: {
            const { GameDistributionProvider } = await import('./providers/game-distribution').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GameDistributionProvider({ ...config, ...settings.options, targeting })
        }
        // TODO: remove when implement provider for tests
        case AdProviderTypes.EMPTY: {
            const { EmptyProvider } = await import('./providers/empty-provider').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new EmptyProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GAM_BANNER_INTERSTITIAL: {
            const { GamBannerInterstitialProvider } = await import('./providers/gam-banner-interstitial').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GamBannerInterstitialProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GOOGLE_AD_SENSE: {
            const { GoogleAdSenseProvider } = await import('./providers/google-ad-sense').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GoogleAdSenseProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GAM_INSTREAM: {
            const { GamInstreamProvider } = await import('./providers/gam-instream').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GamInstreamProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GAM_H5: {
            const { GamH5Provider } = await import('./providers/gam-h5').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GamH5Provider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.APP: {
            const { AppProvider } = await import('./providers/app-provider').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new AppProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.PREBID_DIRECT: {
            const { PrebidDirectProvider } = await import('./providers/prebid-direct').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new PrebidDirectProvider({ ...config, ...settings.options, targeting })
        }
        default:
            throw new Error(`Unsupported ad provider type: "${type}"`)
    }
}

async function initSingleProvider(
    index: number,
    settings: AdProvidersSettings,
    config: ProviderInitConfig,
): Promise<AdProvider | FailedAdProvider> {
    const providerOrder = `${index + 1}`
    const sendRumEvent = config.startRumEvent({
        operationName: 'init-ad-provider',
    })

    let provider: AdProvider | undefined
    try {
        provider = await createProvider(settings, config)
        const result = await provider.serviceStatus

        if (result === AdProviderStatus.online) {
            sendRumEvent({
                status: 'success',
                dimension1: settings.type,
                dimension2: providerOrder,
            })
            return provider
        }
        sendRumEvent({
            status: 'error',
            dimension1: settings.type,
            dimension2: providerOrder,
            dimension3: provider.initErrorMessage ?? 'Provider is offline',
        })
        return createFailedProvider(provider.type, provider.initErrorMessage ?? 'Provider is offline')
    } catch (error) {
        const errorMessage =
            provider?.initErrorMessage ??
            (error instanceof Error ? error.message : `Failed to initialize provider: ${JSON.stringify(error)}`)
        sendRumEvent({
            status: 'error',
            dimension1: settings.type,
            dimension2: providerOrder,
            dimension3: provider?.initErrorMessage ?? errorMessage,
        })
        return createFailedProvider(settings.type, errorMessage)
    }
}

export function resolveProviderNames(settings: AdProvidersSettings[]): string[] {
    const counts = new Map<AdProviderTypes, number>()
    settings.forEach(({ type }) => counts.set(type, (counts.get(type) ?? 0) + 1))
    const seen = new Map<AdProviderTypes, number>()
    return settings.map(({ type }) => {
        const i = (seen.get(type) ?? 0) + 1
        seen.set(type, i)
        return (counts.get(type) ?? 1) > 1 ? `${type}-${i}` : type
    })
}

export function initProviders(
    settings: AdProvidersSettings[],
    config: ProviderInitConfig,
    setProvider: SetProviderCallback,
): void {
    const names = resolveProviderNames(settings)
    settings.forEach((s, index) => {
        initSingleProvider(
            index,
            { ...s, options: { ...s.options, name: names[index] } } as AdProvidersSettings,
            config,
        ).then((provider) => setProvider(index, provider))
    })
}

export async function preloadProviders(settings: AdProvidersSettings[]) {
    const preloads = [...new Set(settings.map((s) => s.type))].map((type) => {
        switch (type) {
            case AdProviderTypes.GAM_H5:
            case AdProviderTypes.GAM_BANNER_INTERSTITIAL:
                return import('./providers/shared/gam-provider-base').then(({ GamProviderBase }) =>
                    GamProviderBase.preload(),
                )
            case AdProviderTypes.GAM_INSTREAM:
                return import('./providers/gam-instream').then(({ GamInstreamProvider }) =>
                    GamInstreamProvider.preload(),
                )
            case AdProviderTypes.PREBID_DIRECT:
                return import('./providers/prebid-direct').then(({ PrebidDirectProvider }) =>
                    PrebidDirectProvider.preload(),
                )
            default:
                return Promise.resolve()
        }
    })

    await Promise.allSettled(preloads)
}
